import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Users, CalendarDays, LogOut, FileEdit, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";

export const StatusDot = ({ status }) => {
    const map = {
        en_cours: "bg-status-encours",
        depart: "bg-status-depart",
        finalise: "bg-status-finalise",
    };
    return <span className={`inline-block w-2 h-2 rounded-full ${map[status] || "bg-white/30"}`} />;
};

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const navCls = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${
            isActive ? "bg-ice-dark text-white" : "text-white/70 hover:bg-ink-600 hover:text-white"
        }`;

    return (
        <div data-testid="dashboard-layout" className="min-h-screen bg-ink-800 text-white flex flex-col">
            {/* Top bar */}
            <header className="border-b border-white/10 bg-ink-900">
                <div className="px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <Logo variant="header" className="h-4 w-auto" />
                        <span className="ml-2 px-3 py-1.5 rounded-md bg-ice-dark text-xs tracking-wider uppercase">
                            Espace consultant
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            data-testid="dashboard-back-site"
                            title="Retour au site"
                            className="hidden sm:inline-flex items-center gap-2 text-sm text-white/60 hover:text-ice transition-colors"
                        >
                            <ArrowLeft size={14} /> Retour au site
                        </Link>
                        <div className="text-right hidden sm:block">
                            <div className="text-sm">{user?.name}</div>
                            <div className="text-xs text-white/50">{user?.email}</div>
                        </div>
                        <div
                            data-testid="user-initials-badge"
                            className="w-10 h-10 rounded-full bg-ice-dark border border-ice/40 flex items-center justify-center text-sm font-semibold"
                        >
                            {user?.initials || "?"}
                        </div>
                        <button
                            data-testid="logout-button"
                            onClick={async () => {
                                await logout();
                                navigate("/login");
                            }}
                            title="Se déconnecter"
                            className="p-2 text-white/60 hover:text-ice transition-colors"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex flex-1">
                {/* Sidebar */}
                <aside className="w-60 border-r border-white/10 bg-ink-900/50 p-4 hidden md:block">
                    <nav className="space-y-1">
                        <NavLink to="/dashboard" end data-testid="sidebar-clients" className={navCls}>
                            <Users size={18} /> Clients
                        </NavLink>
                        <NavLink to="/dashboard/calendrier" data-testid="sidebar-calendrier" className={navCls}>
                            <CalendarDays size={18} /> Calendrier
                        </NavLink>
                        <NavLink to="/dashboard/contenus" data-testid="sidebar-contenus" className={navCls}>
                            <FileEdit size={18} /> Contenus du site
                        </NavLink>
                    </nav>

                    <div className="mt-10">
                        <div className="text-xs tracking-[0.25em] text-white/40 uppercase px-4 mb-3">Statut</div>
                        <div className="px-4 space-y-2.5 text-sm text-white/75">
                            <div className="flex items-center gap-2.5">
                                <StatusDot status="en_cours" /> En cours
                            </div>
                            <div className="flex items-center gap-2.5">
                                <StatusDot status="depart" /> Départ
                            </div>
                            <div className="flex items-center gap-2.5">
                                <StatusDot status="finalise" /> Finalisés
                            </div>
                        </div>
                    </div>
                </aside>

                <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}

export const StatusBadge = ({ status }) => {
    const labels = { en_cours: "En cours", depart: "Départ", finalise: "Finalisé" };
    const cls = {
        en_cours: "bg-status-encours/15 text-status-encours border-status-encours/30",
        depart: "bg-status-depart/15 text-status-depart border-status-depart/30",
        finalise: "bg-status-finalise/15 text-status-finalise border-status-finalise/30",
    }[status];
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
            <StatusDot status={status} /> {labels[status]}
        </span>
    );
};

export const InitialsBadge = ({ value, size = "md", tone = "primary" }) => {
    const sz = { sm: "w-7 h-7 text-[10px]", md: "w-10 h-10 text-sm", lg: "w-14 h-14 text-base" }[size];
    const toneCls = tone === "ice" ? "bg-ice/20 text-ice border-ice/30" : "bg-ice-dark text-white border-ice/30";
    return (
        <div className={`${sz} rounded-full ${toneCls} border flex items-center justify-center font-semibold shrink-0`}>
            {value}
        </div>
    );
};
